/**
 * Griffel styles for ExplanationDialog.
 *
 * Matches the visual structure of the original ADAPT ExplanationDialog
 * (~/adapt/UI/src/Components/DebtCompliance/explanationDialogUtils.ts).
 */

import { makeStyles, tokens } from "@fluentui/react-components";

export const useExplanationDialogStyles = makeStyles({
  dialogSurface: {
    maxWidth: "560px",
    width: "90vw",
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
    paddingTop: "0px",
    overflow: "hidden",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "0px",
  },

  /* ---- Banner header ---- */
  headerBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    marginLeft: `calc(-1 * ${tokens.spacingHorizontalL})`,
    marginRight: `calc(-1 * ${tokens.spacingHorizontalL})`,
    marginBottom: tokens.spacingVerticalS,
  },
  headerBannerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    overflow: "hidden",
  },
  headerAccent: {
    width: "4px",
    height: "28px",
    borderRadius: "2px",
    flexShrink: 0,
  },
  headerBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "12px",
    paddingRight: "12px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    border: "1px solid",
    minWidth: "80px",
    maxWidth: "200px",
    height: "24px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerTypeLabel: {
    fontSize: "0.6875rem",
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    letterSpacing: "0.3px",
  },
  closeButton: {
    minWidth: "auto",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },

  /* ---- Obligor row ---- */
  obligorRow: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  obligorName: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: "1.25",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  obligorId: {
    fontSize: "0.6875rem",
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },

  /* ---- Content sections ---- */
  section: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colorNeutralStroke2,
  },
  sectionLast: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
  },
  sectionLabel: {
    fontSize: "0.6875rem",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: tokens.spacingVerticalS,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },

  /* ---- Key Factors metrics row ---- */
  metricsRow: {
    display: "flex",
    flexWrap: "nowrap",
    overflowX: "auto",
    gap: tokens.spacingHorizontalXS,
  },
  metricCard: {
    display: "flex",
    flexDirection: "column",
    rowGap: "1px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderTopLeftRadius: tokens.borderRadiusSmall,
    borderTopRightRadius: tokens.borderRadiusSmall,
    borderBottomLeftRadius: tokens.borderRadiusSmall,
    borderBottomRightRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
  },
  metricCardLabel: {
    fontSize: "0.625rem",
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
  metricCardValue: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: "1.2",
    whiteSpace: "nowrap",
  },

  /* ---- Explanation bullets ---- */
  bulletList: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  bulletItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: "1.55",
  },
  bulletDot: {
    width: "6px",
    height: "6px",
    minWidth: "6px",
    borderRadius: "50%",
    backgroundColor: tokens.colorBrandBackground,
    marginTop: "6px",
  },
  headlineSentence: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginTop: 0,
    marginBottom: tokens.spacingVerticalS,
    lineHeight: "1.4",
  },

  /* ---- Dialog actions ---- */
  actionsRight: {
    justifyContent: "flex-end",
    paddingTop: tokens.spacingVerticalS,
  },
});
